import { useEffect, useContext, useState, useRef } from "react";
import { useShoppingCart } from "@/context/cart";
import StoreContext from "@/context/store";
import styles from "./Bag.module.scss";
import { useRouter } from "next/router";
import { fetchAuthSession } from "aws-amplify/auth";

export default function Cart({ open }) {
  const [frame, setFrame] = useState(null);
  const { cartItems, setCartItems, changeItemQuantity, increaseCartQuantity, removeFromBag, setCartModal } = useShoppingCart();
  const { store, orderType, address } = useContext(StoreContext);
  const router = useRouter();

  const scriptLoadedRef = useRef(false);
  const skipNextCartSync = useRef(false);
  const storeReady = typeof store === "number" && store > 0;

  const buildCartPayload = async () => {
    const session = await fetchAuthSession();
    const token = session?.tokens?.accessToken?.toString() ?? "";

    return {
      messageType: "buildCart",
      payload: {
        headlessCheckoutPartnerId: "86ec042e-b8d3-4cda-b605-70c2e123f3e2",
        storeId: store,
        reservationMode: orderType?.toLowerCase(),
        cognitoToken: token,
        products: cartItems ?? [],
        deliveryAddress: address && address.street_number
          ? {
              street: `${address.street_number} ${address.route}`,
              city: address.city,
              state_code: address.stateCode,
              zipcode: address.zip,
              lat: address.lat,
              long: address.lng,
            }
          : null,
        options: {
          theme: {
            themeColor: "#663D99",
            navigationColor: "red",
            ctaTextColor: "white",
            backgroundColor: "white",
            calloutBackground: "#379D47",
            calloutTextColor: "white",
            buttonBorderRadius: "24px",
          },
          redirectUrl: "http://plpcsanjose.com/shop",
          disableWeightSelection: true,
          disableLoadingSpinner: false,
          disableAuthFeatures: false,
          disablePrepayment: false,
          kiosk: false,
        },
      },
    };
  };

  const sendCartPayload = async () => {
    if (!frame || !window.cartLoaded || !storeReady) return;
    try {
      const payload = await buildCartPayload();
      frame.contentWindow.postMessage(payload, "*");
      console.log("📤 Sent Jane cart payload:", payload);
    } catch (error) {
      console.error("❌ Error sending cart payload:", error);
    }
  };

  // Only send on cart change when store is ready
  useEffect(() => {
    if (!frame || !window.cartLoaded) return;
    if (skipNextCartSync.current) {
      skipNextCartSync.current = false;
      return;
    }
    sendCartPayload();
  }, [cartItems, store]);

  useEffect(() => {
    const receiveMessage = (event) => {
      const { messageType, payload } = event.data;
      const janeFrame = document.getElementById("jane-menu");
      if (!frame && janeFrame) setFrame(janeFrame);

      if (messageType === "loadingEvent" && payload?.name === "headlessAppLoaded") {
        window.cartLoaded = true;
        sendCartPayload();
      }

      switch (payload?.name) {
        case "cartItemRemoval":
          skipNextCartSync.current = true;
          removeFromBag(payload.properties?.productId);
          break;
        case "cartItemChangeCount":
          skipNextCartSync.current = true;
          changeItemQuantity(
            payload.properties.productId,
            payload.properties.priceId,
            payload.properties.count
          );
          break;
        case "cartEmptied":
          skipNextCartSync.current = true;
          setCartItems([]);
          break;
        case "checkout":
          router.push(`/receipt/${payload.properties.uuid}`);
          const handleRouteChangeComplete = () => {
            setCartItems([]);
            router.events.off("routeChangeComplete", handleRouteChangeComplete);
          };
          router.events.on("routeChangeComplete", handleRouteChangeComplete);

          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({ ecommerce: null });
          window.dataLayer.push({
            event: "purchase",
            ecommerce: {
              transaction_id: payload.properties.cartId,
              value: payload.properties.estimatedTotal,
              currency: "USD",
            },
          });
          break;
        case "drawerClosed":
        case "drawerContinueShopping":
          setCartModal(false);
          break;
        case "cartItemClickedInCheckout":
          if (payload.properties?.productId) {
            router.push(`/product/${payload.properties.productId}`);
          }
          setCartModal(false);
          break;
        case "checkoutPageLoad":
          document.getElementById("cartModal")?.classList.add("fullscreen");
          break;
      }
    };

    window.addEventListener("message", receiveMessage);
    return () => window.removeEventListener("message", receiveMessage);
  }, [frame]);

  useEffect(() => {
    if (document.getElementById("jane-frame-script") || scriptLoadedRef.current) return;

    const loadScript = () => {
      if (scriptLoadedRef.current) return;
      scriptLoadedRef.current = true;

      const script = document.createElement("script");
      script.id = "jane-frame-script";
      script.src = "https://api.iheartjane.com/v2/headless/embed.js";
      script.setAttribute("data-jdid", "user_jdid"); // Replace with actual JDID
      document.getElementById("script-container")?.appendChild(script);

      console.log("🧩 Jane embed script injected");
      removeListeners();
    };

    const removeListeners = () => {
      ["scroll", "click", "mousemove", "touchstart"].forEach((event) =>
        window.removeEventListener(event, loadScript)
      );
    };

    ["scroll", "click", "mousemove", "touchstart"].forEach((event) =>
      window.addEventListener(event, loadScript, { once: true })
    );

    return removeListeners;
  }, []);

  return (
    <div className={styles.bag}>
      <div id="script-container" />
    </div>
  );
}
